import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Drawer,
  Button,
  Toolbar,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Add as AddIcon,
} from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { QuestionInput } from './QuestionInput';
import { Answer } from './Answer';
import { ChatHistoryPanel } from './ChatHistoryPanel';
import {
  ChatMessage,
  conversationApi,
  Citation,
  getUserInfo,
  historyGenerate,
  historyUpdate,
  historyEnsure,
  historyList,
  frontendSettings,
} from '../api';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useAppState } from '../state/AppProvider';

/**
 * Clean citation JSON from message content
 * Azure OpenAI sometimes embeds citation data in the response text
 */
const cleanMessageContent = (content: string): string => {
  // Remove citation JSON objects that look like: {"citations":[...]}
  let cleaned = content.replace(/\{"citations":\[.*?\]\}/gs, '');
  
  // Remove standalone citation objects that look like: {"content":"...","title":"..."}
  cleaned = cleaned.replace(/\{"content":".*?","title":".*?".*?\}/gs, '');
  
  // Clean up any extra whitespace or newlines left behind
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
  
  return cleaned;
};

const useStyles = makeStyles(theme => ({
  chatContainer: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    backgroundColor: theme.palette.background.default,
    position: 'relative',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  toolbar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 56,
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  toolbarActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  messageBox: {
    maxWidth: '80%',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    width: '100%',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  welcomeMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  citationPanel: {
    width: 400,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
  },
  citationPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  citationPanelTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  citationPanelContent: {
    '& p': {
      margin: theme.spacing(1, 0),
    },
    '& pre': {
      backgroundColor: theme.palette.grey[100],
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      overflow: 'auto',
    },
  },
}));

export const Chat = () => {
  const classes = useStyles();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { state, dispatch } = useAppState();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeCitation, setActiveCitation] = useState<Citation | undefined>();
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      // Check authentication
      const authenticated = await getUserInfo();
      setIsAuthenticated(authenticated);

      // Load frontend settings and check CosmosDB
      try {
        const settings = await frontendSettings(discoveryApi, fetchApi);
        console.log('settings', settings);
        dispatch({ type: 'SET_FRONTEND_SETTINGS', payload: settings });

        if (settings?.ui?.show_chat_history_button) {
          // Check CosmosDB availability
          const cosmosHealth = await historyEnsure(discoveryApi, fetchApi);
          console.log('cosmosHealth', cosmosHealth);
          dispatch({ type: 'SET_COSMOSDB_STATUS', payload: cosmosHealth });

          if (cosmosHealth.cosmosDB) {
            // Load chat history
            dispatch({ type: 'FETCH_CHAT_HISTORY_INIT' });
            const conversations = await historyList(discoveryApi, fetchApi, 0);
            if (conversations) {
              dispatch({ type: 'FETCH_CHAT_HISTORY_SUCCESS', payload: conversations });
            } else {
              dispatch({ type: 'FETCH_CHAT_HISTORY_ERROR' });
            }
          }
        }
      } catch (err) {
        console.error('Error initializing chat:', err);
      }
    };

    initialize();
  }, [discoveryApi, fetchApi, dispatch]);

  // Load conversation when selected from history
  useEffect(() => {
    if (state.currentConversation) {
      setMessages(state.currentConversation.messages || []);
      setCurrentConversationId(state.currentConversation.id);
    }
  }, [state.currentConversation]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handler for citation clicks
  const handleCitationClick = (citation: Citation) => {
    setActiveCitation(citation);
    setIsCitationPanelOpen(true);
  };

  // Handler to view citation source
  const handleViewSource = (citation: Citation) => {
    if (citation.url && !citation.url.includes('blob.core')) {
      window.open(citation.url, '_blank');
    }
  };

  // Handler for new chat
  const handleNewChat = () => {
    setMessages([]);
    setCurrentConversationId(null);
    dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: null });
  };

  // Handler for toggling chat history
  const handleToggleHistory = () => {
    dispatch({ type: 'TOGGLE_CHAT_HISTORY' });
  };

  const handleSend = async (question: string) => {
    if (!question.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: question,
      date: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Use history/generate if CosmosDB is available, otherwise use regular conversation
      const useHistory = state.isCosmosDBAvailable.cosmosDB;
      let response;

      if (useHistory) {
        response = await historyGenerate(
          discoveryApi,
          fetchApi,
          { messages: [...messages, userMessage] },
          abortControllerRef.current.signal,
          currentConversationId || undefined,
        );
      } else {
        response = await conversationApi(
          { messages: [...messages, userMessage] },
          abortControllerRef.current.signal,
          discoveryApi,
          fetchApi,
        );
      }

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      // Handle streaming response (NDJSON format from backend)
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let citations: Citation[] = [];
      let toolMessage: ChatMessage | null = null;
      let historyMetadata: any = null;
      let assistantMessageId = Date.now().toString();

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n').filter(line => line.trim());

          for (const line of lines) {
            try {
              const parsed = JSON.parse(line);

              // Extract history metadata if present
              if (parsed.history_metadata) {
                historyMetadata = parsed.history_metadata;
                if (historyMetadata.conversation_id && !currentConversationId) {
                  setCurrentConversationId(historyMetadata.conversation_id);
                }
              }

              // Process messages from the formatted response
              if (parsed.choices && parsed.choices[0]?.messages) {
                const msgs = parsed.choices[0].messages;

                for (const msg of msgs) {
                  if (msg.role === 'tool') {
                    // Store tool message with citations
                    toolMessage = {
                      id: parsed.id || Date.now().toString(),
                      role: 'tool',
                      content: msg.content,
                      date: new Date().toISOString(),
                    };

                    // Extract citations from tool message
                    try {
                      const toolContent = JSON.parse(msg.content);
                      if (toolContent.citations) {
                        citations = toolContent.citations;
                      }
                    } catch (e) {
                      console.debug('Failed to parse tool content:', e);
                    }
                  } else if (msg.role === 'assistant' && msg.content) {
                    // Accumulate assistant message content
                    assistantMessage += msg.content;
                  }
                }
              }

              // Extract message ID if available
              if (parsed.id) {
                assistantMessageId = parsed.id;
              }
            } catch (e) {
              console.debug('Failed to parse NDJSON line:', e);
            }
          }
        }
      }

      // Create assistant message with citations
      const assistantChatMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: cleanMessageContent(assistantMessage || 'No response received'),
        date: new Date().toISOString(),
        citations: citations.length > 0 ? citations : undefined,
      };

      setMessages(prev => [...prev, assistantChatMessage]);

      // Update conversation in CosmosDB if using history
      if (useHistory && (currentConversationId || historyMetadata?.conversation_id)) {
        const convId = currentConversationId || historyMetadata?.conversation_id;
        const messagesToUpdate = [userMessage, assistantChatMessage];
        if (toolMessage) {
          messagesToUpdate.splice(1, 0, toolMessage);
        }

        await historyUpdate(
          discoveryApi,
          fetchApi,
          messagesToUpdate,
          convId,
        );

        // Refresh chat history list
        const conversations = await historyList(discoveryApi, fetchApi, 0);
        if (conversations) {
          dispatch({ type: 'FETCH_CHAT_HISTORY_SUCCESS', payload: conversations });
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Error in conversation:', err);
        setError(err.message || 'Failed to get response');
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  if (!isAuthenticated) {
    return (
      <Box className={classes.chatContainer}>
        <Alert severity="warning">
          Please sign in to use the AI Assistant
        </Alert>
      </Box>
    );
  }

  return (
    <Box className={classes.chatContainer}>
      <Box className={classes.mainContent}>
        {/* Toolbar */}
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6">AI Assistant</Typography>
          <Box className={classes.toolbarActions}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleNewChat}
              variant="outlined"
              size="small"
            >
              New Chat
            </Button>
            {state.frontendSettings?.ui?.show_chat_history_button && (
              <IconButton
                onClick={handleToggleHistory}
                title="Chat History"
                size="small"
              >
                <HistoryIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>

        <Box className={classes.messagesContainer}>
          {messages.length === 0 && (
            <Box className={classes.welcomeMessage}>
              <Typography variant="h5" gutterBottom>
                Welcome to the AI Assistant
              </Typography>
              <Typography variant="body1">
                Ask me anything about AvidXchange development, APIs, or best
                practices.
              </Typography>
            </Box>
          )}

          {messages.filter(message => message.role !== 'tool').map(message => (
            <Box
              key={message.id}
              className={classes.messageBox}
              style={{
                alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              {message.role === 'user' ? (
                <Paper className={classes.userMessage} elevation={2}>
                  <Typography>{message.content}</Typography>
                </Paper>
              ) : (
                <Box className={classes.assistantMessage}>
                  <Answer
                    answer={{
                      answer: message.content,
                      citations: message.citations || [],
                      generated_chart: null,
                      message_id: message.id,
                    }}
                    onCitationClicked={handleCitationClick}
                  />
                </Box>
              )}
            </Box>
          ))}

          {isLoading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">
                Thinking...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Box>

        <QuestionInput
          onSend={handleSend}
          disabled={isLoading}
          placeholder="Ask a question about AvidXchange development..."
        />
      </Box>

      {/* Chat History Side Panel */}
      {state.isChatHistoryOpen && <ChatHistoryPanel />}

      {/* Citation Side Panel */}
      <Drawer
        anchor="right"
        open={isCitationPanelOpen}
        onClose={() => setIsCitationPanelOpen(false)}
        variant="persistent"
      >
        {activeCitation && (
          <Box className={classes.citationPanel}>
            <Box className={classes.citationPanelHeader}>
              <Typography variant="h6">Citations</Typography>
              <IconButton
                onClick={() => setIsCitationPanelOpen(false)}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Typography
              variant="subtitle1"
              className={classes.citationPanelTitle}
              onClick={() => handleViewSource(activeCitation)}
              title={activeCitation.url || activeCitation.title || ''}
            >
              {activeCitation.title || 'Source Document'}
            </Typography>

            <Box className={classes.citationPanelContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {activeCitation.content}
              </ReactMarkdown>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
